import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ImageIcon, Loader2, Trash2, X } from 'lucide-react';
import { slideshowLibraryApi, SlideshowLibraryImage } from '@/lib/api';
import { sortByNatural } from '@/lib/utils';
import { formatFileSize, useLibraryUpload } from '@/hooks/useLibraryUpload';
import { LibraryUploadPanel } from './LibraryUploadPanel';

/**
 * The facility's slideshow Image Library (1.29.0): upload once, add to any
 * slideshow. An image still used by a slideshow can't be deleted — the
 * button says where it's used, and the server refuses with the same names.
 */
export function ImageLibraryTab({ facilityId }: { facilityId?: string | undefined }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['slideshow-library', facilityId],
    queryFn: () => slideshowLibraryApi.list(facilityId),
  });
  const images = sortByNatural(data?.images ?? [], i => i.originalName);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['slideshow-library'] });

  const upload = useLibraryUpload({
    facilityId,
    maxFileSize: data?.maxFileSize ?? null,
    onUploaded: refresh,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => slideshowLibraryApi.remove(id),
    onSuccess: refresh,
    onError: (e: any) => { setError(e.message); refresh(); },
  });

  const handleDelete = (img: SlideshowLibraryImage) => {
    if (!window.confirm(`Delete "${img.originalName}" from the library? The file is removed permanently.`)) return;
    setError(null);
    deleteMutation.mutate(img.id);
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      {/* Upload */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload to Library</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Upload once, then add to any slideshow</p>
        </div>
        <div className="p-6">
          <LibraryUploadPanel upload={upload} />
        </div>
      </div>

      {/* Library */}
      <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Image Library
            {images.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                {images.length} image{images.length !== 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Images in use by a slideshow can't be deleted until they're removed from it.
          </p>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 flex items-start justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              <span className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</span>
              <button onClick={() => setError(null)} className="rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : isError ? (
            <p className="py-12 text-center text-sm text-red-600 dark:text-red-400">
              Failed to load the library: {(queryError as any)?.message ?? 'Unknown error'}
            </p>
          ) : images.length === 0 ? (
            <div className="py-12 text-center">
              <ImageIcon className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">The library is empty</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Upload sponsor images here to use them in any slideshow.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {images.map(img => {
                const inUse = img.usedIn.length > 0;
                return (
                  <div key={img.id} className="flex flex-col rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-900">
                    <div className="mb-2 aspect-[43/10] overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
                      <img
                        src={img.signedUrl}
                        alt={img.originalName}
                        className="h-full w-full object-contain"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200" title={img.originalName}>
                          {img.originalName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(img.fileSize)}
                          {img.width && img.height ? ` · ${img.width}×${img.height}` : ''}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {inUse
                            ? <>Used in {sortByNatural(img.usedIn, u => u.name).map(u => u.name).join(', ')}</>
                            : <span className="italic">Not in any slideshow</span>}
                        </p>
                      </div>
                      <button
                        title={inUse ? 'In use — remove it from its slideshows first' : 'Delete from library'}
                        onClick={() => handleDelete(img)}
                        disabled={inUse || deleteMutation.isPending}
                        className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
